import React, { useRef, useState } from 'react';
import { Description, Dialog, DialogPanel, DialogTitle, DialogBackdrop } from '@headlessui/react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import './DialogSurface.css';

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title = '确认操作',
  message = '确定要执行此操作吗？',
  confirmLabel = '确认',
  cancelLabel = '取消',
  variant = 'danger',
  customButtons = null, // 自定义按钮数组 [{ label, value, variant }]
  zIndex = 100,
}) {
  const isDanger = variant === 'danger';
  const [loading, setLoading] = useState(false);
  const cancelRef = useRef(null);
  const confirmingRef = useRef(false);

  const handleConfirm = async (value) => {
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    setLoading(true);
    try {
      await onConfirm(value);
      onClose();
    } catch (e) {
      // onConfirm 失败时保持 dialog 打开，让调用方处理错误
      console.error('ConfirmDialog onConfirm error:', e);
    } finally {
      confirmingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={loading ? () => {} : onClose}
      className="app-dialog relative"
      initialFocus={cancelRef}
      style={{ zIndex }}
    >
      <DialogBackdrop
        transition
        className="app-dialog__backdrop fixed inset-0"
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          transition
          className="app-dialog__panel relative max-w-sm w-full mx-4 p-6"
          aria-busy={loading}
        >
          <div className="flex items-start gap-3 mb-4">
            {isDanger && (
              <div
                className="app-dialog__icon"
              >
                <AlertTriangle size={18} style={{ color: 'var(--color-error)' }} />
              </div>
            )}
            <div className="min-w-0">
              <DialogTitle
                className="app-dialog__title mb-2"
              >
                {title}
              </DialogTitle>
              <Description className="app-dialog__message">
                {message}
              </Description>
            </div>
          </div>

          {/* 自定义按钮模式 */}
          {customButtons ? (
            <div className="flex flex-col gap-2">
              {customButtons.map((btn, idx) => (
                <button
                  key={idx}
                  disabled={loading}
                  onClick={() => handleConfirm(btn.value)}
                  type="button"
                  className="app-dialog__button"
                  data-intent={btn.variant === 'primary' || btn.variant === 'danger' ? btn.variant : undefined}
                >
                  {loading && <Loader2 size={12} className="animate-spin" />}
                  {btn.label}
                </button>
              ))}
              <button
                onClick={onClose}
                type="button"
                ref={cancelRef}
                disabled={loading}
                className="app-dialog__button"
              >
                {cancelLabel}
              </button>
            </div>
          ) : (
            /* 默认确认/取消按钮 */
            <div className="app-dialog__footer">
              <button
                onClick={onClose}
                type="button"
                ref={cancelRef}
                disabled={loading}
                className="app-dialog__button"
              >
                {cancelLabel}
              </button>
              <button
                disabled={loading}
                type="button"
                onClick={() => handleConfirm()}
                className="app-dialog__button"
                data-intent={isDanger ? 'danger' : 'primary'}
              >
                {loading && <Loader2 size={12} className="animate-spin" />}
                {confirmLabel}
              </button>
            </div>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}
